import { useCallback, useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import useResilientSubscription from '@/hooks/useResilientSubscription';

// A sim reports its first packets before it has published session metadata — the
// opening iRacing frames carry no track or car name. An empty value in a new
// packet must therefore never wipe metadata a populated card is already showing,
// so the rig holds its last valid session until a real value arrives.
const SESSION_FIELDS = ['track', 'car'];

const sortByOrder = (a, b) => (a.sort_order || 0) - (b.sort_order || 0);

function mergeFeed(prev = {}, incoming = {}, id) {
  const merged = { ...prev, ...incoming, id };
  for (const field of SESSION_FIELDS) {
    const next = incoming[field];
    const blank = next == null || (typeof next === 'string' && next.trim() === '');
    if (blank && prev[field]) merged[field] = prev[field];
  }
  return merged;
}

// A fresh snapshot is authoritative about which rigs exist, but the same guard
// applies to it: a rig whose stored track or car is momentarily blank keeps what
// the board is already showing rather than flashing empty.
function mergeSnapshot(prev, snapshot) {
  const map = new Map(prev.map((r) => [r.id, r]));
  const seen = new Set();
  for (const row of snapshot) {
    seen.add(row.id);
    map.set(row.id, mergeFeed(map.get(row.id), row, row.id));
  }
  for (const id of Array.from(map.keys())) if (!seen.has(id)) map.delete(id);
  return Array.from(map.values()).sort(sortByOrder);
}

/**
 * The live rig telemetry feed, shared by the public /live page and the
 * /venue/live display so the realtime behaviour is defined once.
 *
 * Runs on the resilient subscription layer, so a wall display left on overnight
 * reconnects and re-reads by itself instead of freezing on its last values.
 *
 * Returns the rigs in display order, the featured/remaining split and the count
 * of rigs that have reported within the last few seconds.
 */
export default function useLiveRigFeeds() {
  const [rigs, setRigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const resync = useCallback(() => {
    base44.entities.RigLiveFeed.list('-sort_order', 50)
      .then((data) => setRigs((prev) => mergeSnapshot(prev, data || [])))
      .catch(() => { /* a failed re-read keeps the last good board on screen */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { resync(); }, [resync]);

  useResilientSubscription({
    entities: ['RigLiveFeed'],
    onResync: resync,
    onEvent: (event) => {
      setRigs((prev) => {
        const map = new Map(prev.map((r) => [r.id, r]));
        if (event.type === 'delete') {
          map.delete(event.id);
        } else {
          map.set(event.id, mergeFeed(map.get(event.id), event.data, event.id));
        }
        return Array.from(map.values()).sort(sortByOrder);
      });
    },
  });

  // recompute staleness every 2s so cards flip to STALE / STANDBY without new packets
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 2000);
    return () => clearInterval(t);
  }, []);

  const featured = rigs.filter((r) => r.featured && r.theme && r.theme !== 'none');
  const others = rigs.filter((r) => !(r.featured && r.theme && r.theme !== 'none'));
  const liveCount = rigs.filter((r) => r.last_update && (Date.now() - new Date(r.last_update).getTime() < 6000)).length;

  return { rigs, loading, featured, others, liveCount };
}